import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

// "Open the record" in the Approvals inbox lands on a module with
//   navigate('/<module>', { state: { openRecordId, openRequestType, openRecordRef } })
// Each module hands this hook what it does to show one record (switch to its
// list tab, open its view drawer). The hand-over is dropped from the history
// entry straight away, so a refresh shows the plain module.
//
//   useOpenRecordFromLink(async ({ id, ref }) => {
//     const found = await fetchRecord(id, ref, { expense: expensesApi.get });
//     if (!found) return notify(RECORD_GONE, 'error');
//     setActiveTab('list');
//     setViewing(found.record);
//   });

/** Shown when the record a link points at has since gone. */
export const RECORD_GONE = 'This record is no longer available.';

export interface RecordLinkTarget {
  /** The record's database id. */
  id: number;
  /** The approval request's type: CREATE:<module>, EDIT:<module>, DELETE:<module>, BUDGET, ... */
  type?: string;
  /** The request's reference (the record's number, e.g. EXP-12), when there is one. */
  ref?: string;
}

interface LinkState {
  openRecordId?: number | string;
  openRequestType?: string;
  openRecordRef?: string;
}

/**
 * `ready`: false while the module's own records are still loading, for a
 * module that finds the record among them (useMultiLedger) — the link waits.
 */
const useOpenRecordFromLink = (open: (target: RecordLinkTarget) => unknown, ready = true) => {
  const location = useLocation();
  const navigate = useNavigate();
  const openRef = useRef(open);
  openRef.current = open;
  // StrictMode runs an effect twice before the cleared state arrives: act once per entry.
  const handled = useRef<string | null>(null);
  const [target, setTarget] = useState<RecordLinkTarget | null>(null);

  useEffect(() => {
    const st = location.state as LinkState | null;
    if (st?.openRecordId == null || handled.current === location.key) return;
    handled.current = location.key;
    setTarget({ id: Number(st.openRecordId), type: st.openRequestType, ref: st.openRecordRef });
    navigate(location.pathname, { replace: true, state: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  useEffect(() => {
    if (!target || !ready) return;
    setTarget(null);
    Promise.resolve(openRef.current(target)).catch(() => { /* the module reports its own failures */ });
  }, [target, ready]);
};

/** The fewest fields a useMultiLedger row carries. */
interface LedgerLike { rawId?: number; __key: string }

/**
 * Finds a record among a useMultiLedger's rows (active and deleted). Ids repeat
 * across its sibling tables, so the row carrying the request's reference wins.
 */
export const findLedgerRow = <R extends LedgerLike>(rows: R[], id: number, ref?: string, keys?: string[]): R | null => {
  const hits = rows.filter((r) => r.rawId === id && (!keys || keys.includes(r.__key)));
  if (hits.length <= 1 || !ref) return hits[0] || null;
  return hits.find((r) => Object.values(r).some((v) => v != null && String(v) === ref)) || hits[0];
};

/**
 * Fetches one record by id from whichever of a module's resources holds it.
 * Database ids repeat across tables, so when several answer, the one carrying
 * the request's reference (its record number) wins. Null when none has it.
 */
export const fetchRecord = async <K extends string>(
  id: number,
  ref: string | undefined,
  getters: Record<K, (id: number) => Promise<unknown>>
): Promise<{ kind: K; record: any } | null> => {
  const kinds = Object.keys(getters) as K[];
  const found = (await Promise.all(kinds.map(async (kind) => {
    try {
      const record = await getters[kind](id);
      return record && typeof record === 'object' ? { kind, record } : null;
    } catch {
      return null;
    }
  }))).filter(Boolean) as Array<{ kind: K; record: any }>;
  if (found.length <= 1 || !ref || ref.startsWith('#')) return found[0] || null;
  return found.find((f) => Object.values(f.record).some((v) => v != null && String(v) === ref)) || found[0];
};

export default useOpenRecordFromLink;
